import { FC, PropsWithChildren, ReactNode, useCallback, useState } from 'react';
import {
	Box,
	DialogProps,
	IconButton,
	Modal,
	ModalProps,
	SxProps,
	Theme,
	Typography,
	BoxProps,
	Slide,
} from '@mui/material';
import { commonRightBlackModalStyles } from './right-black-modal.styles';
import { CloseIcon } from '../../icons';
import { Button, ButtonProps } from '../buttons';
import { clsx } from '../../utils';

export interface RightBlackModalProps {
	open: boolean;
	modalProps?: Omit<ModalProps, 'open' | 'onClose' | 'children'>;
	headerTitle?: ReactNode;
	headerDescription?: ReactNode;
	submitButtonProps?: ButtonProps;
	submitButtonText?: string;
	showSubmitButton?: boolean;
	footerContent?: ReactNode;
	headerSx?: SxProps<Theme>;
	headerTitleSx?: SxProps<Theme>;
	headerDescriptionSx?: SxProps<Theme>;
	closeButtonSx?: SxProps<Theme>;
	closeIconSx?: SxProps<Theme>;
	contentSx?: SxProps<Theme>;
	footerSx?: SxProps<Theme>;
	animationDuration?: number;
	onClose?: DialogProps['onClose'] | (() => void);
	rootProps?: BoxProps;
	closeDisabled?: boolean;
	contentBoxRef?: React.RefObject<HTMLDivElement>;
	beforeClose?(event: object, reason: 'backdropClick' | 'escapeKeyDown'): boolean;
}

export const RightBlackModal: FC<PropsWithChildren<RightBlackModalProps>> = props => {
	const {
		open,
		onClose,
		modalProps,
		submitButtonProps,
		submitButtonText = 'Save',
		showSubmitButton = true,
		headerDescription,
		headerTitle,
		children,
		headerSx,
		headerTitleSx,
		headerDescriptionSx,
		closeButtonSx,
		closeIconSx,
		contentSx,
		footerSx,
		footerContent,
		rootProps,
		closeDisabled,
		beforeClose = (e, reason) => reason !== 'backdropClick',
		contentBoxRef,
		animationDuration = 300,
	} = props;

	const styles = commonRightBlackModalStyles();
	const [isClosing, setIsClosing] = useState(false);

	const handleClose = useCallback(
		(event: object, reason: 'backdropClick' | 'escapeKeyDown') => {
			if (closeDisabled || isClosing) {
				return;
			}

			const verdict = beforeClose?.(event, reason);

			// undefined and true are considered as "allow closing"
			if (verdict === false) {
				return;
			}

			setIsClosing(true);

			setTimeout(() => {
				setIsClosing(false);
				onClose?.(event, reason);
			}, animationDuration);
		},
		[beforeClose, closeDisabled, onClose, isClosing, animationDuration]
	);

	return (
		<Modal {...modalProps} open={open} onClose={handleClose}>
			<Slide direction="left" in={open && !isClosing} timeout={animationDuration} mountOnEnter unmountOnExit>
				<Box {...rootProps} sx={clsx(styles.root, rootProps?.sx)}>
					<Box sx={clsx(styles.header, headerSx)}>
						<Typography sx={clsx(styles.headerTitle, headerTitleSx)}>{headerTitle}</Typography>
						{headerDescription && (
							<Typography sx={clsx(styles.headerSubtitle, headerDescriptionSx)}>{headerDescription}</Typography>
						)}
						<IconButton
							onClick={e => {
								handleClose?.(e, 'escapeKeyDown');
							}}
							sx={clsx(styles.closeButton, closeButtonSx)}
							disabled={closeDisabled}
						>
							<Box component={CloseIcon} sx={clsx(styles.closeIcon, closeIconSx)} />
						</IconButton>
					</Box>
					<Box sx={clsx(styles.content, contentSx)} ref={contentBoxRef}>
						{children}
					</Box>
					<Box sx={clsx(styles.footer, footerSx)}>
						{showSubmitButton && (
							<Button
								{...submitButtonProps}
								sx={clsx(styles.footerButton, submitButtonProps?.sx)}
								strictColor={submitButtonProps?.strictColor || true}
							>
								{submitButtonText}
							</Button>
						)}
						{footerContent}
					</Box>
				</Box>
			</Slide>
		</Modal>
	);
};
